import { HostRoomBooking } from '../models/HostRoomBooking';
import { RoomBookingStatus } from '../models/RoomBooking';

export type HostRoomBookingDisplayRow =
  | { kind: 'single'; booking: HostRoomBooking }
  | { kind: 'series'; seriesId: string; bookings: HostRoomBooking[] };

/** Empty or unparseable starts are treated as not past. */
export function hostBookingStartIsPast(startIso: string, now: Date): boolean {
  const value = startIso.trim();
  if (!value) return false;
  const normalized = value.endsWith('Z') || value.endsWith('z') ? value : `${value}Z`;
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return false;
  return parsed.getTime() < now.getTime();
}

/** A future session that still needs the host's first accept or decline. */
export function hostBookingGroupsAsPendingSeries(
  booking: HostRoomBooking,
  now: Date,
): boolean {
  const seriesId = booking.recurringSeriesId?.trim();
  if (!seriesId) return false;
  if (booking.status !== RoomBookingStatus.PENDING_CONFIRMATION) return false;
  return !hostBookingStartIsPast(booking.start, now);
}

function startMillis(startIso: string): number {
  const normalized = startIso.endsWith('Z') || startIso.endsWith('z') ? startIso : `${startIso}Z`;
  const parsed = new Date(normalized).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * Two or more future pending sessions of one series stay one card.
 * A lone pending session, and any session already confirmed, declined, or in
 * the past, is its own booking.
 */
export function groupHostRoomBookingRequests(
  items: HostRoomBooking[],
  now: Date = new Date(),
): HostRoomBookingDisplayRow[] {
  if (items.length === 0) return [];
  const bySeries = new Map<string, HostRoomBooking[]>();
  for (const booking of items) {
    if (!hostBookingGroupsAsPendingSeries(booking, now)) continue;
    const seriesId = booking.recurringSeriesId!.trim();
    const list = bySeries.get(seriesId);
    if (list) list.push(booking);
    else bySeries.set(seriesId, [booking]);
  }
  Array.from(bySeries.values()).forEach((list) => {
    list.sort((a, b) => startMillis(a.start) - startMillis(b.start));
  });
  const seriesIds = new Set(
    Array.from(bySeries.entries())
      .filter((entry) => entry[1].length >= 2)
      .map((entry) => entry[0]),
  );
  const emitted = new Set<string>();
  const out: HostRoomBookingDisplayRow[] = [];
  for (const booking of items) {
    const seriesId = booking.recurringSeriesId?.trim();
    const grouped =
      seriesId != null &&
      seriesIds.has(seriesId) &&
      hostBookingGroupsAsPendingSeries(booking, now);
    if (grouped) {
      if (!emitted.has(seriesId)) {
        emitted.add(seriesId);
        out.push({
          kind: 'series',
          seriesId,
          bookings: bySeries.get(seriesId)!,
        });
      }
    } else {
      out.push({ kind: 'single', booking });
    }
  }
  return out;
}

/** Pending sessions on a merged series card are accepted or declined from that card. */
export function hostSeriesCardOwnsRoomActions(
  booking: HostRoomBooking,
  items: HostRoomBooking[],
  now: Date = new Date(),
): boolean {
  if (!hostBookingGroupsAsPendingSeries(booking, now)) return false;
  const seriesId = booking.recurringSeriesId!.trim();
  let count = 0;
  for (const other of items) {
    if (
      other.recurringSeriesId?.trim() === seriesId &&
      hostBookingGroupsAsPendingSeries(other, now)
    ) {
      count += 1;
      if (count >= 2) return true;
    }
  }
  return false;
}
